import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Menu, X, LogOut, LayoutDashboard, ShieldCheck, User } from "lucide-react";
import { InstallAppButton } from "@/components/InstallAppButton";

const NAV_LINKS = [
  { label: "The Journey", to: "/how-it-works" },
  { label: "See how it works", to: "/demo" },
  { label: "Meet the Donkey", to: "/meet-the-donkey" },
  { label: "Why the Donkey?", to: "/about" },
  { label: "Pricing", to: "/pricing" },
  { label: "FAQ", to: "/faq" },
];

const FOCUS_RING =
  "outline-none focus-visible:ring-2 focus-visible:ring-donkey-orange/70 focus-visible:ring-offset-2 focus-visible:ring-offset-donkey-black rounded-md";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [plan, setPlan] = useState(null);
  useEffect(() => {
    if (!user) { setPlan(null); return; }
    api.get("/me/entitlement").then(({ data }) => {
      const e = data.entitlement;
      if (!e) return;
      if (e.entitlement_source === "admin") setPlan("Admin — full access");
      else if (e.effective_plan === "free") setPlan("Free");
      else setPlan(`${e.effective_plan_name} — Active`);
    }).catch(() => {});
  }, [user]);
  const shortId = user ? (user.name || (user.email || "").split("@")[0]) : "";

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 glass border-b border-white/10">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 h-16 flex items-center justify-between">
        <Logo />
        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `text-sm transition-colors px-1 py-0.5 ${FOCUS_RING} ${
                  isActive
                    ? "text-donkey-orange font-medium"
                    : "text-donkey-grey hover:text-donkey-warm"
                }`
              }
              data-testid={`nav-${l.to.replace("/", "")}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <>
              <div className="hidden lg:flex flex-col items-end leading-tight mr-1 max-w-[190px]" data-testid="nav-plan-status">
                <span className="text-xs text-donkey-warm truncate max-w-[190px]" data-testid="nav-identity">{shortId}</span>
                {plan && <span className="text-[11px] text-donkey-orange truncate max-w-[190px]" data-testid="nav-plan-chip">Your plan: {plan}</span>}
              </div>
              {user.role === "admin" && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-donkey-grey hover:text-donkey-warm px-2"
                  onClick={() => navigate("/admin")}
                  aria-label="Admin"
                  data-testid="nav-admin-btn"
                >
                  <ShieldCheck className="w-4 h-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="text-donkey-warm hover:bg-white/5"
                onClick={() => navigate("/dashboard")}
                data-testid="nav-dashboard-btn"
              >
                <LayoutDashboard className="w-4 h-4 mr-1.5" /> Dashboard
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-donkey-warm hover:bg-white/5 px-2"
                onClick={() => navigate("/account")}
                aria-label="Account"
                data-testid="nav-account-btn"
              >
                <User className="w-4 h-4" />
              </Button>
              <InstallAppButton variant="desktop" />
              <Button
                variant="ghost"
                size="sm"
                className="text-donkey-grey hover:text-donkey-warm"
                onClick={doLogout}
                data-testid="nav-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-1.5" /> Sign out
              </Button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="text-sm text-donkey-grey hover:text-donkey-warm transition-colors"
                data-testid="nav-login-link"
              >
                Log in
              </Link>
              <Button
                className="bg-donkey-orange text-donkey-black hover:bg-donkey-orange/90 font-semibold rounded-full px-5"
                onClick={() => navigate("/pricing")}
                data-testid="nav-talk-btn"
              >
                Start with Prove — £29
              </Button>
            </>
          )}
        </div>
        <button
          className="lg:hidden text-donkey-warm"
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden glass border-t border-white/10 px-5 py-4 flex flex-col gap-4">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `py-1 ${FOCUS_RING} ${
                  isActive
                    ? "text-donkey-orange font-medium"
                    : "text-donkey-grey hover:text-donkey-warm"
                }`
              }
              onClick={() => setOpen(false)}
              data-testid={`nav-mobile-${l.to.replace("/", "")}`}
            >
              {l.label}
            </NavLink>
          ))}
          <div className="h-px bg-white/10" />
          <InstallAppButton variant="mobile" />
          {user ? (
            <>
              <div className="rounded-xl border border-white/10 bg-donkey-deep px-4 py-3" data-testid="nav-mobile-identity">
                <p className="text-sm text-donkey-warm truncate">{user.name || user.email}</p>
                {user.name && <p className="text-xs text-donkey-grey truncate">{user.email}</p>}
                {plan && <p className="text-xs text-donkey-orange mt-1" data-testid="nav-mobile-plan">Your plan: {plan}</p>}
              </div>
              <Link to="/dashboard" onClick={() => setOpen(false)} className="text-donkey-warm" data-testid="nav-mobile-dashboard">Dashboard</Link>
              <Link to="/account" onClick={() => setOpen(false)} className="text-donkey-warm" data-testid="nav-mobile-account">Account</Link>
              {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="text-donkey-warm">Admin</Link>}
              <button onClick={doLogout} className="text-left text-donkey-grey flex items-center gap-1.5" data-testid="nav-mobile-logout"><LogOut className="w-4 h-4" /> Sign out</button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={() => setOpen(false)} className="text-donkey-warm">Log in</Link>
              <Button
                className="bg-donkey-orange text-donkey-black font-semibold rounded-full"
                onClick={() => { setOpen(false); navigate("/pricing"); }}
              >
                Start with Prove — £29
              </Button>
            </>
          )}
        </div>
      )}
    </header>
  );
}
